import { useUser } from '@clerk/clerk-react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui'
import { useDashboardStats, useIsMockMode } from '../hooks/useQueries'
import { Thai } from '../utils'
import { SkeletonDashboardCard } from '../components/ui/skeleton'
import { ErrorCard } from '../components/error/ErrorCard'
import { QuickAddContribution } from '../components/forms'

export function DashboardPage() {
  const { user } = useUser()
  const { t } = useTranslation()
  const { data: stats, isLoading, error } = useDashboardStats()
  const isMockMode = useIsMockMode()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">{t('common.loading')}</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {t('dashboard.title')}
            </h1>
            <p className="mt-2 text-gray-600">{t('dashboard.subtitle')}</p>
          </div>
        </div>
        <ErrorCard error={error} onRetry={() => window.location.reload()} />
      </div>
    )
  }

  const dashboardData = stats?.success ? stats.data.data : undefined

  // Format Thai currency - handle both string and number inputs
  const formatCurrency = (amount: string | number) => {
    const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount
    return Thai.Currency.format(isNaN(numericAmount) ? 0 : numericAmount)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {t('common.welcome')}, {user?.firstName || user?.username || t('common.user')}!
          </h1>
          <p className="mt-2 text-gray-600">{t('dashboard.subtitle')}</p>
        </div>
        {isMockMode && (
          <div className="bg-yellow-100 border border-yellow-300 text-yellow-800 px-3 py-1 rounded-md text-sm">
            โหมดทดสอบ (Mock Mode)
          </div>
        )}
      </div>
      
      {/* Stats Grid */}
      {isLoading ? (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <SkeletonDashboardCard />
          <SkeletonDashboardCard />
          <SkeletonDashboardCard />
          <SkeletonDashboardCard />
        </div>
      ) : (
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t('dashboard.totalMembers')}
            </CardTitle>
            <svg
              className="h-4 w-4 text-muted-foreground"
              fill="none"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboardData?.total_members || 0}</div>
            <p className="text-xs text-muted-foreground">
              สมาชิกที่ใช้งาน: {dashboardData?.active_members || 0}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t('dashboard.totalContributions')}
            </CardTitle>
            <svg
              className="h-4 w-4 text-muted-foreground"
              fill="none"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <rect width="20" height="14" x="2" y="5" rx="2" />
              <path d="M2 10h20" />
            </svg>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-600">
              {formatCurrency(dashboardData?.total_contributions || "0")}
            </div>
            <p className="text-xs text-muted-foreground">
              รอยืนยัน: {formatCurrency(dashboardData?.pending_items?.pending_contributions_amount || "0")}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              สินเชื่อคงเหลือ
            </CardTitle>
            <svg
              className="h-4 w-4 text-muted-foreground"
              fill="none"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path d="M12 2v20M2 5l20 14M2 19l20-14" />
            </svg>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {formatCurrency(dashboardData?.total_loans || "0")}
            </div>
            <p className="text-xs text-muted-foreground">
              ค้างชำระ: {dashboardData?.pending_items?.overdue_loans_count || 0} รายการ
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              ยอดคงเหลือปัจจุบัน
            </CardTitle>
            <svg
              className="h-4 w-4 text-muted-foreground"
              fill="none"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {formatCurrency(dashboardData?.current_balance || "0")}
            </div>
            <p className="text-xs text-muted-foreground">
              สมาชิกทั้งหมด: {dashboardData?.total_members || 0} คน
            </p>
          </CardContent>
        </Card>
      </div>
      )}

      {/* Quick Actions */}
      <div className="grid gap-6 md:grid-cols-2">
        <QuickAddContribution />

        <Card>
          <CardHeader>
            <CardTitle>สถานะระบบ</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">โหมดการทำงาน</span>
              <span className={`text-sm font-medium ${isMockMode ? 'text-yellow-600' : 'text-green-600'}`}>
                {isMockMode ? 'ทดสอบ (Mock)' : 'ใช้งานจริง'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">การเชื่อมต่อ</span>
              <span className="text-sm font-medium text-green-600">ปกติ</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">อัปเดตล่าสุด</span>
              <span className="text-sm text-gray-500">
                {Thai.Date.formatBuddhist(new Date())}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}